import { useTranslation } from "react-i18next";
import { prettyLabel } from "src/features/graph/components/DynamicField";

// One row of the per-module issues panel shown at the publication step —
// the same diagnosis rows as the graph page's "problems" panel.
export type CardIssue = {
  key: string;
  severity: "error" | "warning";
  // "unlinked" pulls the row out of the severity groups into its own
  // "Scènes non liées" section, so unlinked scenes read apart from
  // content problems.
  section?: "unlinked";
  messageKey: string;
  messageParams?: Record<string, string | number>;
  fieldKeys?: string[];
  nodeName: string;
  typeLabel: string;
  // 0 = no node to focus (synthetic rows like "unlinked scenes")
  contentId: number;
};

const SECTION_LABEL_KEYS = {
  error: "pages.project.edit.graphIssueErrors",
  warning: "pages.project.edit.graphIssueWarnings",
  unlinked: "pages.project.edit.graphIssueUnlinked",
} as const;

// Same layout/i18n as the graph's issues panel (SVGGraph), but as a fixed
// overlay: clicking a row deep-links to the module's graph, focused on the
// offending node.
const CardIssuesPanel = ({
  issues,
  onSelect,
  onClose,
}: {
  issues: CardIssue[];
  onSelect: (issue: CardIssue) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    // Above the publication banner (z-[100]), below the toast alerts (z-[1000]).
    <div className="fixed inset-0 z-[500]" onMouseDown={onClose}>
      <div className="absolute inset-0 bg-black bg-opacity-30" />
      <div
        className="absolute inset-y-0 right-0 w-96 bg-white border-l border-gray-200 shadow-lg flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <p className="text-sm font-semibold text-gray-700">
              {t("pages.project.edit.graphIssuesTitle")}
            </p>
            <p className="text-[11px] text-gray-400">{t("pages.project.edit.graphIssuesHint")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1"
            aria-label={t("general.close") as string}
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {(["error", "warning", "unlinked"] as const).map((section) => {
            const items = issues.filter((i) => (i.section ?? i.severity) === section);
            if (items.length === 0) return null;
            return (
              <div key={section} className="py-1">
                <p
                  className={`px-4 py-1 text-[11px] font-semibold uppercase ${
                    section === "error" ? "text-red-600" : "text-amber-600"
                  }`}
                >
                  {t(SECTION_LABEL_KEYS[section])} ({items.length})
                </p>
                {items.map((issue) => (
                  <button
                    key={issue.key}
                    className="group flex w-full items-start gap-2 px-4 py-1.5 text-sm text-gray-900 hover:bg-gray-100"
                    onClick={() => onSelect(issue)}
                  >
                    <span
                      className={`flex-shrink-0 text-xs ${
                        section === "error" ? "text-red-500" : "text-amber-500"
                      }`}
                    >
                      ⚠
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-baseline justify-between gap-2">
                        <span
                          className="truncate text-left group-hover:text-green-600"
                          title={issue.nodeName || issue.typeLabel}
                        >
                          {issue.nodeName || issue.typeLabel}
                        </span>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">
                          {issue.typeLabel}
                        </span>
                      </span>
                      <span className="block text-left text-xs text-gray-500 whitespace-normal break-words">
                        {t(
                          `pages.project.edit.graphIssue.${issue.messageKey}`,
                          issue.fieldKeys?.length
                            ? {
                                ...issue.messageParams,
                                fields: issue.fieldKeys
                                  .map((k) => prettyLabel(k, t as any))
                                  .join(", "),
                              }
                            : issue.messageParams,
                        )}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CardIssuesPanel;
